import React, { useEffect, useState } from 'react';
import { fetchActiveEvents, formatTime, RideEventSummary, RidePhase } from '../../services/rideService';

interface EventsSectionProps {
  onSelectEvent: (eventId: string) => void;
}

const PHASE_CONFIG: Record<RidePhase, { label: string; bg: string; pulse?: boolean }> = {
  booking: { label: '', bg: '' },
  in_progress: { label: 'Rides In Progress', bg: 'bg-GREEN', pulse: true },
  completed: { label: 'Event Complete', bg: 'bg-BLUE_AF' },
};

const RidePhaseBadge: React.FC<{ phase?: RidePhase; ridesLeft: number; progress?: { inProgress: number; completed: number; total: number } }> = ({ phase, ridesLeft, progress }) => {
  if (phase === 'in_progress') {
    return (
      <div className={`absolute top-3 right-3 ${PHASE_CONFIG.in_progress.bg} text-WHITE text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1.5`}>
        <span className="w-2 h-2 bg-WHITE rounded-full animate-pulse" />
        {PHASE_CONFIG.in_progress.label}
      </div>
    );
  }

  if (phase === 'completed') {
    return (
      <div className={`absolute top-3 right-3 ${PHASE_CONFIG.completed.bg} text-WHITE text-xs font-bold px-3 py-1 rounded-full`}>
        {PHASE_CONFIG.completed.label}
      </div>
    );
  }

  if (ridesLeft > 0) {
    return (
      <div className="absolute top-3 right-3 bg-ORANGE text-WHITE text-xs font-bold px-3 py-1 rounded-full">
        Free rides available
      </div>
    );
  }

  return (
    <div className="absolute top-3 right-3 bg-WHITE/20 text-WHITE text-xs font-bold px-3 py-1 rounded-full">
      Fully booked
    </div>
  );
};

const EventsSection: React.FC<EventsSectionProps> = ({ onSelectEvent }) => {
  const [events, setEvents] = useState<RideEventSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchActiveEvents()
      .then(setEvents)
      .catch(() => setError('Unable to load events. Please try again.'))
      .finally(() => setLoading(false));
  }, []);

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  };

  return (
    <div className="py-4">
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-WHITE mb-3" style={{ fontFamily: '"Anton", sans-serif' }}>
          Free Rides to Events
        </h1>
        <p className="text-WHITE/70 max-w-xl mx-auto">
          We're giving away free rides to the hottest events. Pick your event, choose a time slot, and we'll get you there. Spots are limited, so book your ride today!
        </p>
      </div>

      {loading && (
        <div className="flex justify-center py-16">
          <div className="w-8 h-8 border-2 border-WHITE border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {error && (
        <div className="text-center py-16">
          <p className="text-red-300 mb-4">{error}</p>
          <button onClick={() => window.location.reload()} className="text-WHITE underline">
            Try again
          </button>
        </div>
      )}

      {!loading && !error && events.length === 0 && (
        <div className="text-center py-16">
          <p className="text-WHITE/60 text-xl">No events with free rides right now.</p>
          <p className="text-WHITE/40 mt-2">Check back soon -- we're always adding new events.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
        {[...events]
          .sort((a, b) => {
            const aCompleted = a.rideProgress?.phase === 'completed' ? 1 : 0;
            const bCompleted = b.rideProgress?.phase === 'completed' ? 1 : 0;
            return aCompleted - bCompleted;
          })
          .map((ev) => {
          const eventImage = ev.display?.heroImage || ev.event?.bannerImage || ev.event?.bannerImages?.medium;
          const isCompleted = ev.rideProgress?.phase === 'completed';

          return isCompleted ? (
            <div
              key={ev._id}
              className="group block bg-BLACK/40 backdrop-blur-sm rounded-2xl overflow-hidden border border-WHITE/10 shadow-xl opacity-90 cursor-default text-left"
            >
              <div className="relative h-64 sm:h-72 md:h-64 lg:h-72 bg-BLACK/30 overflow-hidden">
                {eventImage ? (
                  <img src={eventImage} alt={ev.event?.name || ev.title} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-7xl">
                    {ev.event?.emoji || '🎉'}
                  </div>
                )}
                <RidePhaseBadge phase={ev.rideProgress?.phase} ridesLeft={ev.totalRidesRemaining} progress={ev.rideProgress} />
              </div>

              <div className="p-5">
                <div className="flex items-center gap-2 text-WHITE/50 text-xs mb-1">
                  <span>{formatDate(ev.schedule.date)}</span>
                  <span>·</span>
                  <span>{formatTime(ev.schedule.startTime)} - {formatTime(ev.schedule.endTime)}</span>
                </div>
                <h3 className="text-xl font-bold text-WHITE mb-2">{ev.event?.name || ev.title}</h3>
                {ev.description && <p className="text-WHITE/50 text-sm line-clamp-2">{ev.description}</p>}
                <div className="flex items-center gap-2 mt-2 text-xs text-WHITE/40">
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{ev.pickup?.address || 'You choose pickup location'}</span>
                </div>
              </div>
            </div>
          ) : (
            <button
              key={ev._id}
              onClick={() => onSelectEvent(ev._id)}
              className="group block bg-BLACK/40 backdrop-blur-sm rounded-2xl overflow-hidden border border-WHITE/10 hover:border-WHITE/40 hover:-translate-y-0.5 hover:shadow-2xl transition-all duration-300 text-left"
            >
              <div className="relative h-64 sm:h-72 md:h-64 lg:h-72 bg-BLACK/30 overflow-hidden">
                {eventImage ? (
                  <img src={eventImage} alt={ev.event?.name || ev.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-7xl">
                    {ev.event?.emoji || '🎉'}
                  </div>
                )}
                <RidePhaseBadge phase={ev.rideProgress?.phase} ridesLeft={ev.totalRidesRemaining} progress={ev.rideProgress} />
              </div>

              <div className="p-5">
                <div className="flex items-center gap-2 text-WHITE/50 text-xs mb-1">
                  <span>{formatDate(ev.schedule.date)}</span>
                  <span>·</span>
                  <span>{formatTime(ev.schedule.startTime)} - {formatTime(ev.schedule.endTime)}</span>
                </div>
                <h3 className="text-xl font-bold text-WHITE mb-2 group-hover:text-ORANGE">{ev.event?.name || ev.title}</h3>
                {ev.description && <p className="text-WHITE/50 text-sm line-clamp-2">{ev.description}</p>}
                <div className="flex items-center gap-2 mt-2 text-xs text-WHITE/40">
                  <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="truncate">{ev.pickup?.address || 'You choose pickup location'}</span>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default EventsSection;
